import { useEffect, useState } from "react";
import { toast } from "sonner";

import radioCheck from "assets/svg/radio-check.svg";
import ConfirmMessageModal from "components/ConfirmMessageModal";
import { formatTmNoForDisplay } from "utils/tmNumberDisplay";
import {
  getOppositionProgress,
  getOppositionStampPaperForEvidence,
  OPPOSITION_APPLICANT_TYPE_OPTIONS,
  previewOppositionStampPaperToVendor,
  saveOppositionStampPaperForEvidence,
  sendOppositionStampPaperToVendor,
  OppositionWorkflowStep,
  type OppositionProgressStep,
  type OppositionStampPaperForEvidenceItem,
} from "api/opposition";
import { getStampVendors, type StampVendorOption } from "api/discussion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";

function formatApplicantTypeLabel(value: string | null | undefined): string {
  if (!value) return "";
  const found = OPPOSITION_APPLICANT_TYPE_OPTIONS.find((o) => o.value === value);
  return found?.label ?? value;
}

export const OPPOSITION_STAMP_PAPER_DETAILS_FORM_ID =
  "opposition-stamp-paper-details-form";

const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";
const READONLY_CLASS =
  "h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-[#F7F7F7] px-2.5 py-2 text-[12px] leading-5 text-[#6D6D6D] box-border";
const INPUT_CLASS =
  "h-9 min-h-[44px] md:min-h-0 w-full rounded-[6px] border border-[#EEEEEE] bg-white px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] focus:outline-none focus:ring-1 focus:ring-[#222] box-border";

function RadioCircle({ active }: { active: boolean }) {
  if (active) return <img src={radioCheck} alt="" width={14} height={14} />;
  return (
    <span
      aria-hidden
      className="inline-block w-[14px] h-[14px] rounded-full border border-[#C3C3C3] bg-white"
    />
  );
}

type LineDraft = {
  stampPapers: string;
  vendorName: string;
  vendorPhone: string;
};

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSentStatusChange?: (sent: boolean) => void;
};

export default function OppositionStampPaperDetailsToVendorView({
  leadId,
  onSuccess,
  onSentStatusChange,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [marks, setMarks] = useState<OppositionStampPaperForEvidenceItem[]>([]);
  const [drafts, setDrafts] = useState<Record<string, LineDraft>>({});
  const [activeIdx, setActiveIdx] = useState(0);
  const [applicantContext, setApplicantContext] = useState<{
    applicant: string;
    applicantAddress: string;
    applicantType: string;
    userDate: string;
    signatoryName: string;
    signatoryDesignation: string;
  }>({
    applicant: "",
    applicantAddress: "",
    applicantType: "",
    userDate: "",
    signatoryName: "",
    signatoryDesignation: "",
  });
  const [hasSent, setHasSent] = useState(false);
  const [stampVendors, setStampVendors] = useState<StampVendorOption[]>([]);
  const [previewMessage, setPreviewMessage] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMode, setConfirmMode] = useState<"send" | "resend">("send");
  const [resending, setResending] = useState(false);
  const [sendingAfterConfirm, setSendingAfterConfirm] = useState(false);

  useEffect(() => {
    onSentStatusChange?.(hasSent);
  }, [hasSent, onSentStatusChange]);

  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getOppositionStampPaperForEvidence(leadId),
      getOppositionProgress(leadId).catch(() => null),
      getStampVendors().catch((): StampVendorOption[] => []),
      previewOppositionStampPaperToVendor(leadId).catch(
        (): { active: boolean; message: string } => ({ active: false, message: "" })
      ),
    ])
      .then(([data, progress, vendors, preview]) => {
        if (cancelled) return;
        setMarks(data.conflictingMarks);
        setStampVendors(vendors ?? []);
        const next: Record<string, LineDraft> = {};
        data.conflictingMarks.forEach((m) => {
          next[m.id] = {
            stampPapers:
              m.stampPapersForEvidence != null
                ? String(m.stampPapersForEvidence)
                : "",
            vendorName: m.stampVendorNameForEvidence ?? "",
            vendorPhone: m.stampVendorPhoneForEvidence ?? "",
          };
        });
        setDrafts(next);
        setApplicantContext({
          applicant: data.applicant ?? "",
          applicantAddress: data.applicantAddress ?? "",
          applicantType: data.applicantType ?? "",
          userDate: data.userDate ?? "",
          signatoryName: data.signatoryName ?? "",
          signatoryDesignation: data.signatoryDesignation ?? "",
        });
        setActiveIdx(0);
        setPreviewMessage(preview?.message ?? null);
        if (progress) {
          const step = progress.steps.find(
            (s: OppositionProgressStep) =>
              s.slug ===
              OppositionWorkflowStep.OP_S3_08a_StampPaperDetailsSentToVendor
          );
          setHasSent(Boolean(step?.isCompleted));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load stamp paper details"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const activeMark = marks[activeIdx] ?? null;
  const activeDraft = activeMark ? drafts[activeMark.id] : null;

  const updateActiveDraft = (patch: Partial<LineDraft>) => {
    if (!activeMark) return;
    setDrafts((prev) => ({
      ...prev,
      [activeMark.id]: { ...prev[activeMark.id], ...patch },
    }));
  };

  const handleVendorSelect = (value: string) => {
    if (value === "__none__") {
      updateActiveDraft({ vendorName: "", vendorPhone: "" });
      return;
    }
    const vendor = stampVendors.find((v) => v.name === value);
    updateActiveDraft({
      vendorName: value,
      vendorPhone: vendor?.phone ?? "",
    });
  };

  const vendorSelectValue =
    activeDraft?.vendorName &&
    stampVendors.some((v) => v.name === activeDraft.vendorName)
      ? activeDraft.vendorName
      : "__none__";

  const validate = (): boolean => {
    if (marks.length === 0) {
      toast.error("No eligible applications. Mark proceedWithEvidence=YES in Step 5 first.");
      return false;
    }
    for (const m of marks) {
      const d = drafts[m.id];
      if (!d) continue;
      const n = Number(d.stampPapers);
      if (!d.stampPapers.trim() || !Number.isInteger(n) || n < 1) {
        toast.error(
          `Enter a valid number of stamp papers for ${m.applicationNo ?? "application"}.`
        );
        return false;
      }
      if (!d.vendorName.trim()) {
        toast.error(
          `Enter Stamp Vendor name for ${m.applicationNo ?? "application"}.`
        );
        return false;
      }
      if (!d.vendorPhone.trim()) {
        toast.error(
          `Enter Stamp Vendor phone for ${m.applicationNo ?? "application"}.`
        );
        return false;
      }
    }
    return true;
  };

  const persist = async (): Promise<boolean> => {
    if (!leadId) return false;
    if (!validate()) return false;
    try {
      const fresh = await saveOppositionStampPaperForEvidence(leadId, {
        applications: marks.map((m) => ({
          conflictingMarkId: m.id,
          stampPapersForEvidence: Number(drafts[m.id]?.stampPapers ?? 0),
          stampVendorNameForEvidence: (drafts[m.id]?.vendorName ?? "").trim(),
          stampVendorPhoneForEvidence: (drafts[m.id]?.vendorPhone ?? "").trim(),
        })),
      });
      setMarks(fresh.conflictingMarks);
      return true;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save stamp paper details");
      return false;
    }
  };

  const refreshPreview = async (): Promise<string | null> => {
    if (!leadId) return null;
    try {
      const res = await previewOppositionStampPaperToVendor(leadId);
      const message = res?.message ?? "";
      setPreviewMessage(message || null);
      return message;
    } catch {
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    setSaving(true);
    try {
      const ok = await persist();
      if (!ok) return;
      if (hasSent) {
        toast.success("Stamp paper details saved.");
        onSuccess?.(leadId);
        return;
      }
      const message = await refreshPreview();
      if (!message?.trim()) {
        toast.error("Unable to build vendor message. Verify the details and try again.");
        return;
      }
      setConfirmMode("send");
      setShowConfirmModal(true);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmProceed = () => {
    if (!leadId || sendingAfterConfirm) return;
    setShowConfirmModal(false);
    setSendingAfterConfirm(true);
    (async () => {
      try {
        await sendOppositionStampPaperToVendor(leadId);
        setHasSent(true);
        toast.success(
          confirmMode === "resend"
            ? "Stamp paper details resent to vendor via WhatsApp."
            : "Stamp paper details sent to vendor WhatsApp."
        );
        onSuccess?.(leadId);
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Failed to send to vendor"
        );
      } finally {
        setSendingAfterConfirm(false);
      }
    })();
  };

  const handleResend = async () => {
    if (!leadId || resending || saving) return;
    setResending(true);
    try {
      const ok = await persist();
      if (!ok) return;
      const message = await refreshPreview();
      if (!message?.trim()) {
        toast.error("Unable to build vendor message. Verify the details and try again.");
        return;
      }
      setConfirmMode("resend");
      setShowConfirmModal(true);
    } finally {
      setResending(false);
    }
  };

  if (!leadId) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  if (marks.length === 0) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          No eligible applications. Mark proceedWithEvidence=YES in Step 5
          (Confirmation from Client for Evidence) first.
        </p>
      </div>
    );
  }

  return (
    <form
      id={OPPOSITION_STAMP_PAPER_DETAILS_FORM_ID}
      onSubmit={handleSubmit}
      className="flex w-full max-w-[1085px] flex-col gap-5"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {marks.length > 1 && (
        <div className="flex flex-row flex-wrap gap-3">
          {marks.map((m, idx) => {
            const isActive = idx === activeIdx;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => setActiveIdx(idx)}
                className={`flex items-center gap-2 px-2.5 py-2.5 rounded-[6px] h-10 min-w-[140px] transition-colors text-xs ${
                  isActive
                    ? "bg-[#222222] text-white"
                    : "bg-[#F7F7F7] text-[#222222]"
                }`}
              >
                <RadioCircle active={isActive} />
                {formatTmNoForDisplay(m.applicationNo ?? "")}
              </button>
            );
          })}
        </div>
      )}

      {/* Row: Number of Stamp Papers | Quick select vendor */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>
            Number of Stamp Papers needed for Evidence
          </label>
          <input
            type="number"
            min={1}
            step={1}
            value={activeDraft?.stampPapers ?? ""}
            onChange={(e) => updateActiveDraft({ stampPapers: e.target.value })}
            placeholder="Text Here"
            className={INPUT_CLASS}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>
            Quick select vendor (optional)
          </label>
          <Select value={vendorSelectValue} onValueChange={handleVendorSelect}>
            <SelectTrigger className="h-9 min-h-[44px] md:min-h-0 w-full border border-[#EEEEEE] rounded-[6px] px-2.5 py-2 text-[12px] leading-5 text-[#222222] data-[placeholder]:text-[#6D6D6D]">
              <SelectValue placeholder="Select to autofill" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— Add new vendor —</SelectItem>
              {stampVendors
                .filter(
                  (v) => typeof v.name === "string" && v.name.trim().length > 0
                )
                .map((v) => (
                  <SelectItem key={v.name} value={v.name}>
                    {v.name}
                    {v.isDefault ? " (default)" : ""}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Row: Vendor Name | Vendor Phone */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Name of Stamp Vendor for Evidence</label>
          <input
            type="text"
            value={activeDraft?.vendorName ?? ""}
            onChange={(e) => updateActiveDraft({ vendorName: e.target.value })}
            placeholder="Text Here"
            className={INPUT_CLASS}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>
            Phone of Stamp Vendor for Evidence
          </label>
          <input
            type="tel"
            inputMode="numeric"
            value={activeDraft?.vendorPhone ?? ""}
            onChange={(e) => updateActiveDraft({ vendorPhone: e.target.value })}
            placeholder="e.g. 9876543210"
            className={INPUT_CLASS}
          />
        </div>
      </div>

      {/* Row: Applicant | Applicant Address */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Applicant</label>
          <input
            type="text"
            value={applicantContext.applicant}
            placeholder="Set in Status Details for Counter"
            readOnly
            className={READONLY_CLASS}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Applicant Address</label>
          <input
            type="text"
            value={applicantContext.applicantAddress}
            placeholder="Set in Status Details for Counter"
            readOnly
            className={READONLY_CLASS}
          />
        </div>
      </div>

      {/* Row: Applicant Type | User Date */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Applicant Type</label>
          <input
            type="text"
            value={formatApplicantTypeLabel(applicantContext.applicantType)}
            placeholder="—"
            readOnly
            className={READONLY_CLASS}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>User Date</label>
          <input
            type="text"
            value={applicantContext.userDate}
            placeholder="—"
            readOnly
            className={READONLY_CLASS}
          />
        </div>
      </div>

      {/* Row: Signatory's Name | Signatory's Designation */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Signatory&apos;s Name</label>
          <input
            type="text"
            value={applicantContext.signatoryName}
            placeholder="—"
            readOnly
            className={READONLY_CLASS}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className={LABEL_CLASS}>Signatory&apos;s Designation</label>
          <input
            type="text"
            value={applicantContext.signatoryDesignation}
            placeholder="—"
            readOnly
            className={READONLY_CLASS}
          />
        </div>
      </div>

      <ConfirmMessageModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        previewContent={previewMessage || "—"}
        onConfirm={handleConfirmProceed}
        disableConfirm={!previewMessage?.trim() || sendingAfterConfirm}
      />

      {hasSent && (
        <div className="flex items-center gap-3 mt-2">
          <button
            type="button"
            disabled={resending || saving}
            onClick={handleResend}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? "Resending…" : "Resend Message"}
          </button>
        </div>
      )}
    </form>
  );
}
